import React from 'react';
import classNames from 'classnames';
import {useTheme} from '@mui/material/styles';
import {Tooltip} from '@mui/material';
import {IntlShape} from 'react-intl';
import {Lx} from '../../../../i18n/consts';
import styles from './time-report-employee-total-bar.module.scss';

type MetricTone = 'approved' | 'billable' | 'regular';

type Metric = {
    key: string;
    label: string;
    value: string;
    tooltip?: string;
    tone?: MetricTone;
    tightLabel?: boolean;
};

type Props = {
    intl: IntlShape;
    workedHours?: number;
    overtimeHours?: number;
    absenceHours?: number;
    approvedHours?: number;
    billableHours?: number;
    timeBankBalance?: number;
    plannedHours?: number;
    className?: string;
};

const formatHours = (value?: number): string => {
    if (value === undefined || value === null) {
        return '0';
    }

    return (Math.round(value * 10) / 10).toString();
};

const formatOptionalHours = (value?: number): string => {
    if (value === undefined || value === null) {
        return '';
    }

    return formatHours(value);
};

const TimeReportEmployeeTotalBar: React.FC<Props> = ({
    intl,
    workedHours,
    overtimeHours,
    absenceHours,
    approvedHours,
    billableHours,
    timeBankBalance,
    plannedHours,
    className
}) => {
    const theme = useTheme();
    const colorStyles = React.useMemo<React.CSSProperties>(() => ({
        '--time-report-total-bar-bg': theme.palette.background?.default ?? '#121212',
        '--time-report-total-bar-divider': theme.palette.divider ?? 'rgba(255, 255, 255, 0.12)',
        '--time-report-total-bar-text': theme.palette.text?.primary ?? 'rgba(255, 255, 255, 0.04)',
        '--time-report-total-bar-secondary-text': theme.palette.text?.secondary ?? 'rgba(255, 255, 255, 0.56)',
        '--time-report-total-bar-approved': theme.palette.warning?.main ?? '#f5cd47',
        '--time-report-total-bar-billable': theme.palette.success?.main ?? '#59ca59'
    } as React.CSSProperties), [theme]);

    const metrics: Metric[] = [
        ...(plannedHours !== undefined ? [{
            key: 'planned',
            label: `${intl.formatMessage({
                id: Lx.Schedule.PLANNED_HOURS_ABBREVIATION,
                defaultMessage: 'PL'
            })}:`,
            value: formatOptionalHours(plannedHours),
            tooltip: intl.formatMessage({id: Lx.Schedule.PLANNED_HOURS}),
            tone: 'regular' as MetricTone
        }] : []),
        {
            key: 'worked',
            label: `${intl.formatMessage({
                id: Lx.Schedule.WORKED_HOURS_ABBREVIATION,
                defaultMessage: 'WH'
            })}:`,
            value: formatHours(workedHours),
            tooltip: intl.formatMessage({id: Lx.Schedule.WORKED_HOURS}),
            tone: 'regular'
        },
        {
            key: 'overtime',
            label: `${intl.formatMessage({
                id: Lx.Schedule.OVERTIME_HOURS_ABBREVIATION,
                defaultMessage: 'OV'
            })}:`,
            value: formatHours(overtimeHours),
            tooltip: intl.formatMessage({id: Lx.Schedule.OVERTIME_HOURS}),
            tone: 'regular'
        },
        {
            key: 'absence',
            label: `${intl.formatMessage({
                id: Lx.Schedule.ABSENCE_HOURS_ABBREVIATION,
                defaultMessage: 'AH'
            })}:`,
            value: formatHours(absenceHours),
            tooltip: intl.formatMessage({id: Lx.Schedule.ABSENCE_HOURS})
        },
        {
            key: 'timeBank',
            label: `${intl.formatMessage({
                id: Lx.Schedule.TIME_BANK_BALANCE_ABBREVIATION,
                defaultMessage: 'TB'
            })}:`,
            value: formatHours(timeBankBalance),
            tooltip: intl.formatMessage({ id: Lx.Schedule.TIME_BANK_BALANCE, defaultMessage: 'Time bank balance' }),
            tone: 'regular'
        },
        {
            key: 'approved',
            label: `${intl.formatMessage({
                id: Lx.Schedule.APPROVED_HOURS,
                defaultMessage: 'Approved'
            })}:`,
            value: formatHours(approvedHours),
            tone: 'approved'
        },
        {
            key: 'billable',
            label: `${intl.formatMessage({
                id: Lx.Schedule.BILLABLE_HOURS,
                defaultMessage: 'Billable'
            })}:`,
            value: formatHours(billableHours),
            tone: 'billable'
        }
    ];

    const hasPlanned = plannedHours !== undefined;

    return (
        <div
            className={classNames(styles.totalBar, className, { [styles.totalBarWithoutPlanned]: !hasPlanned })}
            style={colorStyles}
        >
            {metrics.map((metric, index) => (
                <div
                    key={metric.key}
                    className={classNames(styles.metric, {
                        [styles.metricWithDivider]: index !== 0,
                        [styles.metricTight]: metric.tightLabel
                    })}
                >
                    {metric.tooltip ? (
                        <Tooltip title={metric.tooltip}>
                            <span className={classNames(styles.metricLabel, { [styles.metricLabelTight]: metric.tightLabel })}>{metric.label}</span>
                        </Tooltip>
                    ) : (
                        <span className={classNames(styles.metricLabel, { [styles.metricLabelTight]: metric.tightLabel })}>{metric.label}</span>
                    )}
                    <span
                        className={classNames(styles.metricValue, {
                            [styles.metricValueRegular]: metric.tone === 'regular',
                            [styles.metricValueApproved]: metric.tone === 'approved',
                            [styles.metricValueBillable]: metric.tone === 'billable'
                        })}
                    >
                        {metric.value}
                    </span>
                </div>
            ))}
        </div>
    );
};

export default TimeReportEmployeeTotalBar;
